/**
 * Semantic status tones for the redesign.
 *
 * The mockups colour status by MEANING rather than by enum value: anything
 * live/settled is green, anything pending is amber, anything failed is red,
 * anything finished-and-filed is neutral grey. `<StatusChip>` renders the pill;
 * this module owns the mapping.
 *
 * Kept in its own module (not inside StatusChip.tsx) so the component file
 * exports only components — the react-refresh lint rule requires that.
 */

/** Semantic buckets the mockups use. `neutral` is the grey "inactive" pill. */
export type StatusTone =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'primary'
  | 'neutral';

/**
 * Maps a tow-request / claim status token to its semantic tone.
 *
 * Status tokens come from the .NET enum and are uppercase on the wire
 * (OPEN / WINNER / TOARRIVE / INPROCESS / COMPLETED / CLOSE / CANCEL).
 * Unknown values fall through to `neutral` so unrecognised statuses still
 * render a legible pill rather than throwing.
 */
export function statusTone(status: string | null | undefined): StatusTone {
  if (status === null || status === undefined || status === '') return 'neutral';
  switch (status.toUpperCase()) {
    case 'OPEN':       return 'info';
    // A tow request the driver is still filling in, or never sent.
    case 'DRAFT':      return 'neutral';
    case 'INCOMING':   return 'warning';
    case 'ABANDONED':  return 'neutral';
    case 'NOBIDS':     return 'error';
    // Route trip statuses (ROUTINGS.OPTIONS.status).
    case 'PLANNED':    return 'info';
    case 'CANCELLED':  return 'error';
    // Offers are out and stacked. Deliberately not the same tone as WINNER:
    // somebody being offered a job is not somebody having accepted one, and
    // that is exactly the distinction this screen exists to make.
    case 'OFFERED':    return 'info';
    case 'WINNER':     return 'primary';
    case 'ASSIGNING':  return 'warning';
    case 'TOARRIVE':   return 'warning';
    case 'INPROCESS':  return 'warning';
    case 'PENDING':    return 'warning';
    case 'ONSCENE':    return 'success';
    case 'LIVE':       return 'success';
    case 'COMPLETED':  return 'success';
    case 'APPROVED':   return 'success';
    case 'ACTIVE':     return 'success';
    case 'CANCEL':     return 'error';
    case 'DECLINED':   return 'error';
    case 'REJECTED':   return 'error';
    case 'CLOSE':      return 'neutral';
    case 'CLOSED':     return 'neutral';
    case 'INACTIVE':   return 'neutral';
    default:           return 'neutral';
  }
}
